<script setup lang="ts">
import type { Category, Tag } from '~/types/blog'
import { normalizeFieldBool, normalizeFieldJson, normalizeFieldText } from '~/composables/useCustomFields'
import { resolvePagePath, resolvePostPath } from '~/composables/usePermalink'
import { STORAGE_KEYS } from '~/utils/storage'

const blogApi = useBlogApi()
const siteData = await useSiteData()
const route = useRoute()
const DETAIL_SIDEBAR_STORAGE_KEY = STORAGE_KEYS.DETAIL_SIDEBAR_COLLAPSED
const detailSidebarCollapsed = useState('blog-detail-sidebar-collapsed', () =>
  !normalizeFieldBool(siteData?.setting?.customFields, 'sidebarDefaultExpanded', true)
)
const sidebarReady = ref(false)
const postDetailMatched = computed(() => !!resolvePostPath(siteData?.setting?.customFields, route.path))
const pageDetailMatched = computed(() => !!resolvePagePath(siteData?.setting?.customFields, route.path))
const contentDetailMatched = computed(() => postDetailMatched.value || pageDetailMatched.value)
const showTagCloud = computed(() => normalizeFieldBool(siteData?.setting?.customFields, 'showTagCloud', true))
const { data: sidebarData } = await useAsyncData(
  () => `blog-sidebar:${showTagCloud.value ? 'tags-on' : 'tags-off'}`,
  async () => {
    const [categories, pages, tags] = await Promise.all([
      blogApi.getCategoryTree(),
      blogApi.getContentMetaList({ type: 'page', page: 1, pageSize: 200 }),
      showTagCloud.value ? blogApi.getTagCloud() : Promise.resolve([] as Tag[])
    ])
    return { categories, tags, pages: pages.list || [] }
  },
  { watch: [showTagCloud] }
)
const siteName = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'siteName'))
const siteSubtitle = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'siteSubtitle', ''))
const siteLogo = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'siteLogo', ''))
const showNavSiteName = computed(() => normalizeFieldBool(siteData?.setting?.customFields, 'showNavSiteName', true))
const isSideNav = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'navMode', 'top') === 'side')
const icp = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'icp', ''))
const publicSecurityRecord = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'publicSecurityRecord', ''))

const categories = computed<Category[]>(() => sidebarData.value?.categories || [])
const tags = computed<Tag[]>(() => sidebarData.value?.tags || [])
const footerText = computed(() => normalizeFieldText(siteData?.setting?.customFields, 'footerText', 'Powered by Yuelai Engine Blog'))
const footerExternalLinks = computed(() =>
  normalizeFieldJson<Array<string | { label?: string; url?: string }>>(siteData?.setting?.customFields, 'footerExternalLinks', [])
)
const showFeedLink = computed(() => normalizeFieldBool(siteData?.setting?.customFields, 'showFeedLink', true))
const showSitemapLink = computed(() => normalizeFieldBool(siteData?.setting?.customFields, 'showSitemapLink', true))
const navContentLimit = computed(() => Math.max(1, Number(normalizeFieldText(siteData?.setting?.customFields, 'navContentLimit', '10')) || 10))
const navItems = computed(() => {
  const items = normalizeFieldJson<Array<{ name?: string; path?: string; sort?: number; newTab?: boolean }>>(
    siteData?.setting?.customFields,
    'navItems',
    []
  )
  return items
    .filter(item => String(item?.name || '').trim() && String(item?.path || '').trim())
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
})
const pageLinks = computed(() =>
  [...(sidebarData.value?.pages || [])]
    .sort((a, b) => {
      const sortDiff = (a.sort || 0) - (b.sort || 0)
      if (sortDiff !== 0) {
        return sortDiff
      }
      return (a.ID || 0) - (b.ID || 0)
    })
)
const detailSidebarToggleLabel = computed(() => (detailSidebarCollapsed.value ? '展开侧栏' : '收起侧栏'))
const { items: contentTocItems, floating: contentTocFloating, activeId: contentTocActiveId } = useContentTocState()
const showFloatingContentToc = computed(() => contentDetailMatched.value && contentTocFloating.value && contentTocItems.value.length > 0)

onMounted(async () => {
  try {
    const raw = localStorage.getItem(DETAIL_SIDEBAR_STORAGE_KEY)
    if (raw === '1' || raw === '0') {
      detailSidebarCollapsed.value = raw === '1'
    }
  } catch {
    // Ignore localStorage failures (private mode/quota).
  }
  await nextTick()
  document.documentElement.removeAttribute('data-sidebar-collapsed')
  sidebarReady.value = true
})

watch(detailSidebarCollapsed, value => {
  if (!import.meta.client) {
    return
  }
  try {
    localStorage.setItem(DETAIL_SIDEBAR_STORAGE_KEY, value ? '1' : '0')
  } catch {
    // Ignore localStorage failures (private mode/quota).
  }
})
</script>

<template>
  <div class="site-shell" :class="{ 'site-shell-side': isSideNav, 'sidebar-ready': sidebarReady }">
    <BlogSiteHeader
      :site-name="siteName"
      :site-subtitle="siteSubtitle"
      :site-logo="siteLogo"
      :show-site-name-in-nav="showNavSiteName"
      :custom-fields="siteData?.setting?.customFields"
      :nav-items="navItems"
      :page-links="pageLinks"
      :categories="categories"
      :nav-content-limit="navContentLimit"
    />
    <button
      type="button"
      class="detail-sidebar-toggle"
      :aria-pressed="detailSidebarCollapsed"
      :aria-label="detailSidebarToggleLabel"
      :title="detailSidebarToggleLabel"
      @click="detailSidebarCollapsed = !detailSidebarCollapsed"
    >
      <span class="detail-sidebar-toggle-icon" aria-hidden="true">
        <Transition name="detail-sidebar-icon-swap" :mode="sidebarReady ? 'out-in' : undefined" :css="sidebarReady">
          <i
            :key="detailSidebarCollapsed ? 'collapsed' : 'expanded'"
            :class="detailSidebarCollapsed ? 'fa-solid fa-angles-left' : 'fa-solid fa-angles-right'"
          />
        </Transition>
      </span>
    </button>
    <div class="site-body">
      <main :class="['container', 'page-wrap', { 'page-wrap-collapsed': detailSidebarCollapsed }]">
        <section class="main-pane">
          <slot />
        </section>
        <aside :class="['side-pane', { 'side-pane-collapsed': detailSidebarCollapsed }]">
          <div class="side-pane-inner">
            <div :class="['side-pane-backdrop', { 'side-pane-backdrop-muted': showFloatingContentToc }]">
              <BlogSidebar :categories="categories" :tags="tags" />
            </div>
            <Transition name="side-floating-toc-fade">
              <div v-if="showFloatingContentToc" class="side-floating-toc">
                <BlogPostToc :items="contentTocItems" :active-id="contentTocActiveId" variant="aside" />
              </div>
            </Transition>
          </div>
        </aside>
      </main>
      <BlogSiteFooter
        :text="footerText"
        :icp="icp"
        :public-security-record="publicSecurityRecord"
        :external-links="footerExternalLinks"
        :show-feed-link="showFeedLink"
        :show-sitemap-link="showSitemapLink"
      />
    </div>
    <BlogBackToTop />
  </div>
</template>

<style scoped>
.site-shell:not(.sidebar-ready) .page-wrap,
.site-shell:not(.sidebar-ready) .side-pane,
.site-shell:not(.sidebar-ready) .side-pane-inner {
  transition: none;
}

.page-wrap {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 20px;
  /* 裁切侧栏展开过程中超出网格列宽的内容，避免产生横向滚动条 */
  overflow-x: clip;
  margin-top: 30px;
  margin-bottom: 46px;
  transition:
    grid-template-columns 0.28s ease,
    gap 0.28s ease;
}

.main-pane {
  min-width: 0;
}

.detail-sidebar-toggle {
  --detail-sidebar-toggle-size: 176px;
  --detail-sidebar-toggle-peek: 32px;
  position: fixed;
  top: 50%;
  right: calc(var(--detail-sidebar-toggle-peek) - var(--detail-sidebar-toggle-size));
  z-index: 42;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  border: 0;
  border-radius: 999px;
  box-sizing: border-box;
  width: var(--detail-sidebar-toggle-size);
  height: var(--detail-sidebar-toggle-size);
  padding: 0 0 0 calc((var(--detail-sidebar-toggle-peek) - 24px) / 2);
  color: var(--text-soft);
  background: var(--bg-soft);
  box-shadow: var(--floating-button-shadow);
  cursor: pointer;
  overflow: hidden;
  transform: translateY(-50%);
  transition:
    color 0.18s ease,
    background 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.28s ease;
}

.detail-sidebar-toggle:hover {
  color: var(--brand);
  background: var(--floating-button-hover-bg);
  transform: translateY(-50%) translateX(-6px);
}

.detail-sidebar-toggle-icon {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 24px;
}

.detail-sidebar-toggle-icon i {
  font-size: 15px;
  line-height: 1;
}

.detail-sidebar-icon-swap-enter-active,
.detail-sidebar-icon-swap-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.2s ease;
}

.detail-sidebar-icon-swap-enter-from {
  opacity: 0;
  transform: translateX(6px) scale(0.9);
}

.detail-sidebar-icon-swap-leave-to {
  opacity: 0;
  transform: translateX(-6px) scale(0.9);
}

.page-wrap-collapsed {
  grid-template-columns: minmax(0, 1fr) 0;
  gap: 0;
}

.side-pane {
  min-width: 0;
  align-self: stretch;
  overflow: visible;
  opacity: 1;
  transform: translateX(0);
  transition:
    opacity 0.24s ease,
    transform 0.28s ease;
}

.side-pane-inner {
  width: 300px;
  min-height: 100%;
  display: grid;
  align-items: start;
  opacity: 1;
  transform: translateX(0);
  transition:
    opacity 0.24s ease,
    transform 0.28s ease;
}

.side-pane-backdrop,
.side-floating-toc {
  grid-area: 1 / 1;
  min-width: 0;
}

.side-pane-backdrop {
  transition:
    opacity 0.22s ease,
    filter 0.22s ease,
    transform 0.22s ease;
}

.side-pane-backdrop-muted {
  opacity: 0.42;
  filter: blur(2px);
  transform: scale(0.985);
  pointer-events: none;
}

.side-floating-toc {
  position: sticky;
  top: calc(var(--anchor-offset) + 14px);
  z-index: 2;
}

.side-floating-toc-fade-enter-active,
.side-floating-toc-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.22s ease;
}

.side-floating-toc-fade-enter-from,
.side-floating-toc-fade-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.side-pane-collapsed {
  overflow: hidden;
  opacity: 0;
  transform: translateX(24px);
  pointer-events: none;
}

.side-pane-collapsed .side-pane-inner {
  opacity: 0;
  transform: translateX(16px);
}

@media (max-width: 960px) {
  .page-wrap {
    grid-template-columns: 1fr;
    margin-top: 94px;
  }

  .side-pane {
    display: none;
  }

  .detail-sidebar-toggle {
    display: none;
  }
}

@media (min-width: 961px) {
  html[data-sidebar-collapsed='1'] .detail-sidebar-toggle-icon i {
    --fa: '\f100';
  }

  html[data-sidebar-collapsed='0'] .detail-sidebar-toggle-icon i {
    --fa: '\f101';
  }

  html[data-sidebar-collapsed='1'] .page-wrap {
    grid-template-columns: minmax(0, 1fr) 0;
    gap: 0;
  }

  html[data-sidebar-collapsed='1'] .side-pane {
    overflow: hidden;
    opacity: 0;
    transform: translateX(24px);
    pointer-events: none;
  }

  html[data-sidebar-collapsed='1'] .side-pane-inner {
    opacity: 0;
    transform: translateX(16px);
  }

  html[data-sidebar-collapsed='0'] .page-wrap {
    grid-template-columns: minmax(0, 1fr) 300px;
    gap: 20px;
  }

  html[data-sidebar-collapsed='0'] .side-pane {
    overflow: visible;
    opacity: 1;
    transform: translateX(0);
    pointer-events: auto;
  }

  html[data-sidebar-collapsed='0'] .side-pane-inner {
    opacity: 1;
    transform: translateX(0);
  }
}
</style>
